import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { X, MapPin, AlertCircle, CheckCircle2, Loader2, Home, Search } from 'lucide-react';
import { clientsAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

export default function AddressCleanupModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('formatting'); // 'formatting', 'zipcodes', 'unresolved', 'geocoding'

  // Fetch address issues
  const { data: formattingData, isLoading: isLoadingFormatting, refetch: refetchFormatting } = useQuery(
    'addressFormattingIssues',
    () => clientsAPI.getAddressFormattingIssues(),
    { enabled: isOpen && activeTab === 'formatting' }
  );

  // Fetch zip code issues
  const { data: zipCodeData, isLoading: isLoadingZipCodes, refetch: refetchZipCodes } = useQuery(
    'zipCodeIssues',
    () => clientsAPI.getZipCodeIssues(),
    { enabled: isOpen && activeTab === 'zipcodes' }
  );

  // Fetch unresolved zip codes
  const { data: unresolvedData, isLoading: isLoadingUnresolved, refetch: refetchUnresolved } = useQuery(
    'unresolvedZipCodes',
    () => clientsAPI.getUnresolvedZipCodes(),
    { enabled: isOpen && activeTab === 'unresolved' }
  );

  // Fetch geocoding failures
  const { data: geocodingFailuresData, isLoading: isLoadingGeocodingFailures, refetch: refetchGeocodingFailures } = useQuery(
    'geocodingFailures',
    () => clientsAPI.getGeocodingFailures(),
    { enabled: isOpen && activeTab === 'geocoding' }
  );

  // Standardize address mutation
  const standardizeAddressMutation = useMutation(
    ({ clientId, address }) => clientsAPI.standardizeAddress(clientId, { address }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients');
        refetchFormatting();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to standardize address');
      }
    }
  );

  // Resolve zip code mutation
  const resolveZipCodeMutation = useMutation(
    ({ clientId, zipCode }) => clientsAPI.resolveZipCode(clientId, { zipCode }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients');
        refetchZipCodes();
        refetchUnresolved();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to resolve zip code');
      }
    }
  );

  // Auto-resolve zip codes mutation
  const autoResolveZipCodesMutation = useMutation(
    () => clientsAPI.autoResolveZipCodes(),
    {
      onSuccess: (data) => {
        toast.success(`Resolved ${data.data?.resolved || 0} zip codes automatically`);
        queryClient.invalidateQueries('clients');
        refetchZipCodes();
        refetchUnresolved();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to auto-resolve zip codes');
      }
    }
  );

  // Format address helper
  const formatAddress = (address) => {
    if (!address) return '';
    // Convert to title case
    return address
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Handle common abbreviations
        const abbreviations = ['st', 'ave', 'rd', 'dr', 'blvd', 'ln', 'ct', 'pl', 'pkwy', 'hwy', 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
        if (abbreviations.includes(word)) {
          return word.toUpperCase();
        }
        // Capitalize first letter
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  const handleStandardizeAddress = async (clientId, currentAddress) => {
    const formatted = formatAddress(currentAddress);
    if (formatted === currentAddress) {
      toast.info('Address is already properly formatted');
      return;
    }
    
    try {
      await standardizeAddressMutation.mutateAsync({ clientId, address: formatted });
      toast.success('Address standardized successfully');
    } catch (error) {
      // Error handled in mutation
    }
  };

  const handleResolveZipCode = async (clientId, zipCode) => {
    try {
      await resolveZipCodeMutation.mutateAsync({ clientId, zipCode });
      toast.success('Zip code updated successfully');
    } catch (error) {
      // Error handled in mutation
    }
  };

  if (!isOpen) return null;

  const formattingIssues = formattingData?.data?.issues || [];
  const zipCodeIssues = zipCodeData?.data?.issues || [];
  const unresolvedZipCodes = unresolvedData?.data?.clients || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Address Cleanup
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Standardize addresses and resolve zip code issues
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('formatting')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'formatting'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Formatting Issues ({formattingIssues.length})
              </button>
              <button
                onClick={() => setActiveTab('zipcodes')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'zipcodes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Zero Zip Codes ({zipCodeIssues.length})
              </button>
              <button
                onClick={() => setActiveTab('unresolved')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'unresolved'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Unresolved ({unresolvedZipCodes.length})
              </button>
              <button
                onClick={() => setActiveTab('geocoding')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'geocoding'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Geocoding Failures ({geocodingFailuresData?.data?.clients?.length || 0})
              </button>
            </nav>
          </div>

          {/* Body */}
          <div className="px-4 py-5 sm:p-6 max-h-[60vh] overflow-y-auto">
            {activeTab === 'formatting' && (
              <>
                {isLoadingFormatting ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading formatting issues...</span>
                  </div>
                ) : formattingIssues.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Formatting Issues</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      All addresses appear to be properly formatted.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-blue-900">
                            Found {formattingIssues.length} address(es) with formatting issues
                          </p>
                          <p className="mt-1 text-sm text-blue-700">
                            Addresses will be converted to proper title case format.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {formattingIssues.map((issue) => (
                        <div key={issue.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{issue.companyName}</p>
                              <p className="text-xs text-gray-500 mt-1">{issue.city}, {issue.state}</p>
                              <div className="mt-2 flex items-start space-x-2">
                                <div className="flex-1">
                                  <p className="text-xs text-gray-500 mb-1">Current:</p>
                                  <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{issue.address}</p>
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs text-gray-500 mb-1">Formatted:</p>
                                  <p className="text-sm text-gray-900 bg-blue-50 p-2 rounded font-medium">
                                    {formatAddress(issue.address)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleStandardizeAddress(issue.id, issue.address)}
                              disabled={standardizeAddressMutation.isLoading}
                              className="ml-4 btn btn-primary btn-sm"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'zipcodes' && (
              <>
                {isLoadingZipCodes ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading zip code issues...</span>
                  </div>
                ) : zipCodeIssues.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Zip Code Issues</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      All zip codes appear to be valid.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-amber-900">
                              Found {zipCodeIssues.length} customer(s) with zero or invalid zip codes
                            </p>
                            <p className="mt-1 text-sm text-amber-700">
                              Attempting to resolve using address, city, and state information.
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => autoResolveZipCodesMutation.mutate()}
                          disabled={autoResolveZipCodesMutation.isLoading}
                          className="btn btn-primary btn-sm"
                        >
                          {autoResolveZipCodesMutation.isLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Resolving...
                            </>
                          ) : (
                            <>
                              <Search className="h-4 w-4 mr-2" />
                              Auto-Resolve All
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {zipCodeIssues.map((issue) => (
                        <div key={issue.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{issue.companyName}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {issue.address}, {issue.city}, {issue.state}
                              </p>
                              <div className="mt-2 flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Current Zip:</span>
                                <span className="text-sm text-red-600 font-medium">{issue.zipCode || '00000'}</span>
                                {issue.suggestedZipCode && (
                                  <>
                                    <span className="text-xs text-gray-400">→</span>
                                    <span className="text-sm text-green-600 font-medium">{issue.suggestedZipCode}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {issue.suggestedZipCode && (
                              <button
                                onClick={() => handleResolveZipCode(issue.id, issue.suggestedZipCode)}
                                disabled={resolveZipCodeMutation.isLoading}
                                className="ml-4 btn btn-primary btn-sm"
                              >
                                Apply
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'unresolved' && (
              <>
                {isLoadingUnresolved ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading unresolved zip codes...</span>
                  </div>
                ) : unresolvedZipCodes.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Unresolved Zip Codes</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      All zip codes have been resolved.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-red-900">
                            {unresolvedZipCodes.length} customer(s) require manual zip code resolution
                          </p>
                          <p className="mt-1 text-sm text-red-700">
                            These addresses could not be automatically resolved. Please update manually.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {unresolvedZipCodes.map((client) => (
                        <div key={client.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{client.companyName}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {client.address}, {client.city}, {client.state}
                              </p>
                              <div className="mt-2">
                                <span className="text-xs text-gray-500">Current Zip:</span>
                                <span className="ml-2 text-sm text-red-600 font-medium">{client.zipCode || '00000'}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                if (onEditClient) {
                                  onEditClient(client);
                                  onClose();
                                } else {
                                  toast.info('Please edit this customer manually to update the zip code');
                                }
                              }}
                              className="ml-4 btn btn-secondary btn-sm"
                            >
                              Edit Manually
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'geocoding' && (
              <>
                {isLoadingGeocodingFailures ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading geocoding failures...</span>
                  </div>
                ) : (geocodingFailuresData?.data?.clients || []).length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Geocoding Failures</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      All customers have been successfully geocoded.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-red-900">
                            {(geocodingFailuresData?.data?.clients || []).length} customer(s) could not be geocoded
                          </p>
                          <p className="mt-1 text-sm text-red-700">
                            These addresses may be invalid or incomplete. Please verify and update them.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {(geocodingFailuresData?.data?.clients || []).map((client) => (
                        <div key={client.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{client.companyName}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {client.address}, {client.city}, {client.state} {client.zipCode || ''}
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                if (onEditClient) {
                                  onEditClient(client);
                                  onClose();
                                } else {
                                  toast.info('Please edit this customer manually to fix the address');
                                }
                              }}
                              className="ml-4 btn btn-secondary btn-sm"
                            >
                              Edit Manually
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}






